<template>
  <div class="row page-section">
    <div class="col-12">
      <div class="row section-header">
        <div class="col-xs-12 col-lg-6">
          <h1>{{ 'order_positions' | i18n }}</h1>
        </div>
        <div class="col-xs-12 col-lg-6">
          <div class="action-buttons">
            <b-button v-if="canEdit" v-b-modal.modal-new-position variant="primary" size="sm"> + {{ 'new' | i18n }} </b-button>
            <b-dropdown v-if="canEdit" variant="outline-primary" right :disabled="!selectedPositions.length" class="actions-dropdown" size="sm">
              <b-dropdown-item @click="deleteSelectedPositions">
                {{ 'delete' | i18n }}
              </b-dropdown-item>
              <b-dropdown-item :disabled="!selectedPosition" @click="showPositionUpdateModal(selectedPosition)">
                {{ 'edit' | i18n }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12  main-content">
          <b-table
            id="order-positions-table"
            ref="table"
            table-class="table-filtered"
            :fields="fields"
            :items="orderPositions"
            :per-page="perPage"
            :current-page="currentPage"
            :filter="filter"
            :filter-function="filterRows"
            :responsive="true"
            :busy="isLoading"
            :sticky-header="true"
            :show-empty="true"
            :empty-text="noTableDataTitle"
            :empty-filtered-text="noTableDataTitle"
            fixed
            @filtered="onFiltered"
          >
            <template v-if="canEdit" #head(check)>
              <b-form-checkbox id="checkbox__all" v-model="allSelected" :checked="allSelected" name="checkbox__all" @change="toggleSelectAll" />
            </template>
            <template #head(articleNumber)="row">
              {{ row.label }}
              <table-filter v-model="filter.articleNumber" />
            </template>
            <template #head(articleMatchCode)="row">
              {{ row.label }}
              <table-filter v-model="filter.articleMatchCode" />
            </template>
            <template #head(articleDescription)="row">
              {{ row.label }}
              <table-filter v-model="filter.articleDescription" />
            </template>
            <template v-if="canEdit" #cell(check)="row">
              <b-form-checkbox
                :id="'checkbox__' + row.item.id"
                v-model="row.item.isSelected"
                :checked="row.item.isSelected || allSelected"
                :name="'checkbox__' + row.item.id"
                @change="toggleSelectPosition(row.item)"
              />
            </template>
            <template #cell(localPrice)="row">
              {{ row.item.localPrice | formatPrice }}
            </template>
            <template #cell(priceInEur)="row">
              {{ row.item.priceInEur | formatPrice }}
            </template>
            <template #table-busy>
              <div class="text-center">
                <b-spinner class="align-middle" />
                <strong class="ml-1">{{ 'loading' | i18n }}...</strong>
              </div>
            </template>
          </b-table>
          <b-pagination v-model="currentPage" class="table-pagination" :total-rows="totalRows" :per-page="perPage" size="sm" />
        </div>
      </div>
    </div>
    <update-position ref="updatePositionModal" @updated="onPositionUpdated" />
    <new-position v-if="orderId" :order-id="orderId" @position-created="onPositionCreated" />
  </div>
</template>

<script>
import { NotificationMixin, OrderMixin, TableFilterMixin, DeleteRowMixin } from '../../mixins';
import UpdatePosition from './update-position';
import TableFilter from '../table/table-filter';
import { NO_TABLE_DATA_TITLE } from '../../../../../constants';
import { BTable } from 'bootstrap-vue';
import NewPosition from './new-position';

export default {
  name: 'OrderPositions',
  components: { NewPosition, TableFilter, UpdatePosition, 'b-table': BTable },
  mixins: [NotificationMixin, OrderMixin, TableFilterMixin, DeleteRowMixin],
  props: {
    orderId: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      orderPositions: [],
      currentPage: 1,
      perPage: 100,
      totalRows: 0,
      isLoading: false,
      allSelected: false,
      fields: [
        { key: 'check', label: '', thClass: 'check', tdClass: 'check' },
        { key: 'articleNumber', label: this.$translate('number_short'), thClass: 'article-number', tdClass: 'article-number' },
        { key: 'articleMatchCode', label: this.$translate('matchcode'), thClass: 'article-matchcode', tdClass: 'article-matchcode' },
        { key: 'articleDescription', label: this.$translate('article_title'), thClass: 'article-description', tdClass: 'article-description' },
        { key: 'quantity', label: this.$translate('amount'), thClass: ['quantity', 'number-column'], tdClass: ['quantity', 'number-column'] },
        { key: 'localPrice', label: this.$translate('price'), thClass: ['price', 'number-column'], tdClass: ['price', 'number-column'] },
        { key: 'priceInEur', label: `${this.$translate('price')} / €`, thClass: ['price', 'number-column'], tdClass: ['price', 'number-column'] },
        {
          key: 'priceChangedByUser',
          label: this.$translate('priceChangedByUser'),
          thClass: ['price', 'number-column'],
          tdClass: ['price', 'number-column']
        },
        { key: 'inner', label: this.$translate('inner'), thClass: 'inner', tdClass: 'inner' },
        { key: 'outer', label: this.$translate('outer'), thClass: 'outer', tdClass: 'outer' },
        { key: 'minPrice', label: this.$translate('min_price'), thClass: ['price', 'number-column'], tdClass: ['price', 'number-column'] },
        { key: 'bkz', label: this.$translate('evaluation_number_short'), thClass: 'bkz', tdClass: 'bkz' }
      ],
      filter: {
        articleNumber: null,
        articleMatchCode: null,
        articleDescription: null
      },
      selectedPosition: null,
      noTableDataTitle: this.$translate(NO_TABLE_DATA_TITLE)
    };
  },
  computed: {
    selectedPositions: function() {
      return this.orderPositions.filter(p => p.isSelected);
    }
  },
  created() {
    this.getOrderPositions();
  },
  methods: {
    async getOrderPositions() {
      this.isLoading = true;
      const orderId = this.order ? this.order.id : this.$route.params.id;
      const response = await this.orderService.getOrderPositions(orderId);
      this.orderPositions = response.positions.map(position => ({ ...position, isSelected: false }));
      this.totalRows = this.orderPositions.length;
      this.isLoading = false;
      this.selectedPosition = null;
    },
    async deleteSelectedPositions() {
      await this.deleteSelectedRows(
        {
          message: this.$translate('position_delete_confirmation_message'),
          rowDisplayValues: this.selectedPositions.map(p => p.articleNumber)
        },
        async () => {
          await this.orderService.deleteOrderPositions(
            this.orderId,
            this.selectedPositions.map(p => p.id)
          );
          await this.getOrderPositions();
        }
      );
    },
    showPositionUpdateModal(position) {
      this.$refs.updatePositionModal.show({
        id: position.id,
        orderId: this.orderId,
        articleNumber: position.articleNumber,
        quantity: position.quantity
      });
    },
    async onPositionUpdated() {
      await this.getOrderPositions();
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    toggleSelectPosition(position) {
      const isSelected = !position.isSelected;
      const prevSelectedPositions = this.selectedPositions;
      const newSelectedPositions = isSelected ? prevSelectedPositions.concat([position]) : prevSelectedPositions.filter(p => p.id !== position.id);

      if (newSelectedPositions.length === 1) {
        this.selectedPosition = isSelected ? position : newSelectedPositions[0];
        this.allSelected = false;
      } else {
        this.selectedPosition = null;
      }
    },
    toggleSelectAll() {
      this.allSelected = !this.allSelected;
      this.orderPositions.forEach(p => (p.isSelected = this.allSelected));
      this.selectedPosition = null;
    },
    async onPositionCreated() {
      await this.getOrderPositions();
    }
  }
};
</script>

<style scoped lang="scss">
.main-content {
  padding: 0;
}

::v-deep   .table {
  min-width: 950px;

  @media (max-width: 1440px) {
    font-size: 0.813rem;
  }

  th,
  td {
    &.check {
      width: 5%;
    }

    &.article-number {
      width: 10%;
    }

    &.article-matchcode {
      width: 10%;
    }

    &.quantity {
      width: 6%;
    }

    &.price {
      width: 8%;
    }

    &.article-description {
      width: 30%;
    }

    &.inner {
      width: 5%;
    }

    &.outer {
      width: 5%;
    }

    &.inner,
    &.outer {
      text-align: right;
    }

    &.bkz {
      width: 5%;
    }
  }
}

.b-table-sticky-header {
  max-height: 400px;
}
</style>
